import classNames from 'classnames';
import { arc, scaleLinear } from 'd3';
import { useCallback, useId, useMemo } from 'react';

import styles from './gauge-chart.module.css';

const WIDTH_UNITS = 188;
const HEIGHT_UNITS = 86;
const HALF_WIDTH = WIDTH_UNITS / 2;
const HALF_HEIGHT = HEIGHT_UNITS / 2;
const OUTER_RADIUS = WIDTH_UNITS / 1.4;
const INNER_RADIUS = OUTER_RADIUS * 0.945;

const START_ANGLE = -Math.PI / 4;
const END_ANGLE = Math.PI / 4;
const BUG_LENGTH = 0.1;
const HALF_BUG_LENGTH = BUG_LENGTH / 2;

const START_COLOR = '#55A3FF';
const END_COLOR = '#FF646D';

export type GaugeChartProps = {
  mainText?: string;
  unit?: string;
  subText?: string;
  minLabel?: string;
  maxLabel?: string;
  minValue?: number;
  maxValue?: number;
  colorRange?: string[];
  value: number;
};

export const GaugeChart = ({
  mainText,
  unit,
  subText,
  minLabel,
  maxLabel,
  minValue = 0,
  maxValue = 100,
  colorRange = [START_COLOR, END_COLOR],
  value,
}: GaugeChartProps) => {
  const gradientId = useId();

  const pathD = useMemo(
    () =>
      arc().cornerRadius(100)({
        innerRadius: INNER_RADIUS,
        outerRadius: OUTER_RADIUS,
        startAngle: START_ANGLE,
        endAngle: END_ANGLE,
      }),
    []
  );

  const bugScale = useCallback(
    (value: number) => {
      const angleScale = scaleLinear()
        .domain([minValue, maxValue])
        .range([START_ANGLE + HALF_BUG_LENGTH, END_ANGLE - HALF_BUG_LENGTH])
        .clamp(true);

      const arcGenerator = arc().cornerRadius(100);

      return arcGenerator({
        innerRadius: INNER_RADIUS,
        outerRadius: OUTER_RADIUS,
        startAngle: angleScale(value) - HALF_BUG_LENGTH,
        endAngle: angleScale(value) + HALF_BUG_LENGTH,
      });
    },
    [minValue, maxValue]
  );

  const colors = useMemo(
    () => (colorRange.length > 1 ? colorRange : [START_COLOR, END_COLOR]),
    [colorRange]
  );

  const colorScale = useMemo(
    () =>
      scaleLinear<string>()
        .domain(
          colors.map(
            (_, i) =>
              minValue + (i / (colors.length - 1)) * (maxValue - minValue)
          )
        )
        .range(colors)
        .clamp(true),

    [colors, minValue, maxValue]
  );

  const bugD = useMemo(() => bugScale(value), [bugScale, value]);

  const hasEndLabels = !!(minLabel || maxLabel);

  // Arc tip midpoint (average of inner/outer radius) at ±PI/4
  const midRadius = (INNER_RADIUS + OUTER_RADIUS) / 2;
  const tipOffsetX = midRadius * Math.sin(Math.PI / 4);
  const tipY = OUTER_RADIUS - midRadius * Math.cos(Math.PI / 4);
  const labelLeftX = HALF_WIDTH - tipOffsetX;
  const labelRightX = HALF_WIDTH + tipOffsetX;
  const labelY = tipY + 10;

  // Horizontal padding so end-labels aren't clipped
  const LABEL_PAD = 30;
  const vbX = hasEndLabels ? -LABEL_PAD : 0;
  const vbW = hasEndLabels ? WIDTH_UNITS + LABEL_PAD * 2 : WIDTH_UNITS;
  const vbH = hasEndLabels ? HEIGHT_UNITS + 10 : HEIGHT_UNITS;

  return (
    <div
      className={styles['meterContainer']}
      {...(hasEndLabels && {
        style: { aspectRatio: `${vbW} / ${vbH}` },
      })}
    >
      <svg width="100%" height="100%" viewBox={`${vbX} -6 ${vbW} ${vbH}`}>
        <defs>
          <linearGradient id={gradientId} x1="-10%" y1="0%" x2="110%" y2="0%">
            {colors.map((color, index) => (
              <stop
                key={index}
                offset={`${(index / (colors.length - 1)) * 100}%`}
                stopColor={color}
                stopOpacity={1}
              />
            ))}
          </linearGradient>
        </defs>
        <g transform={`translate(${HALF_WIDTH}, ${OUTER_RADIUS})`}>
          {pathD && (
            <path
              d={pathD}
              fill={`url(#${gradientId})`}
              className={styles['path']}
            />
          )}
          {bugD && <path d={bugD} fill={colorScale(value)} />}
        </g>
        <g
          transform={`translate(${HALF_WIDTH + (unit ? 6 : 0)}, ${HALF_HEIGHT + 6})`}
        >
          <text
            textAnchor={unit ? 'end' : 'middle'}
            alignmentBaseline="baseline"
            className={classNames(styles['text'], styles['mainText'])}
          >
            {mainText}
          </text>
          {unit && (
            <text
              x={4}
              alignmentBaseline="baseline"
              className={classNames(styles['text'], styles['unit'])}
            >
              {unit}
            </text>
          )}
        </g>
        <g transform={`translate(${HALF_WIDTH}, ${HALF_HEIGHT + 14})`}>
          <text
            alignmentBaseline="before-edge"
            className={classNames(styles['text'], styles['subText'])}
          >
            {subText}
          </text>
        </g>
        {minLabel && (
          <text
            textAnchor="middle"
            className={classNames(styles['text'], styles['endLabel'])}
          >
            {minLabel.split(' ').map((word, i) => (
              <tspan key={i} x={labelLeftX} dy={i === 0 ? labelY : '1.2em'}>
                {word}
              </tspan>
            ))}
          </text>
        )}
        {maxLabel && (
          <text
            textAnchor="middle"
            className={classNames(styles['text'], styles['endLabel'])}
          >
            {maxLabel.split(' ').map((word, i) => (
              <tspan key={i} x={labelRightX} dy={i === 0 ? labelY : '1.2em'}>
                {word}
              </tspan>
            ))}
          </text>
        )}
      </svg>
    </div>
  );
};
